import { Badge, MenuItem } from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { useCreditFacilityEventDelete } from "features/credit-facility/CreditFacilityEventDelete";
import { CreditFacilityEventHeaderCard } from "features/credit-facility/CreditFacilityEventHeaderCard";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import {
  PageWizardContextValues,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const CreditFacilityEventLayout: React.FC<
  React.PropsWithChildren<{
    canEditEvent: boolean;
  }>
> = ({ children, canEditEvent }) => {
  const { t } = useTranslation(["common", "creditFacility", "enums"]);
  const navigate = useNavigate();

  const { fundFamilyId, fundFamilyEventId, creditFacilityId } =
    useRequiredStringParams();

  const { data: fundFamilyEvent } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const backToSummary = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .creditFacilities({})
        .creditFacility({ creditFacilityId }).$,
    );
  }, [navigate, fundFamilyId, creditFacilityId]);

  const confirmDeleteEvent = useCreditFacilityEventDelete(
    fundFamilyId,
    creditFacilityId,
    backToSummary,
  );
  const badge = getCapitalActivityEventStateBadge(fundFamilyEvent.state, t);

  const pageWizardContext = usePageWizard() as
    | PageWizardContextValues
    | undefined;

  const isDraft = fundFamilyEvent.state == "draft";
  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        documentTitle={pageWizardContext?.currentPage?.breadcrumb}
        workflowName={
          isDraft
            ? t(
                `creditFacility:eventCard.workflowName.${fundFamilyEvent.sub_type as "drawdown" | "paydown" | "reallocation" | "merge_tranches"}`,
              )
            : undefined
        }
        title={
          pageWizardContext?.currentPage?.title ||
          t(`enums:FundFamilyEventSubTypeEnum.${fundFamilyEvent.sub_type!}`)
        }
        backButton={
          !isDraft
            ? {
                title: t("creditFacility:title"),
                path: rootRoute({})
                  .fundFamilies({})
                  .fundFamily({ fundFamilyId })
                  .creditFacilities({})
                  .creditFacility({ creditFacilityId }).$,
              }
            : undefined
        }
        stats={
          <CreditFacilityEventHeaderCard
            creditFacilityEvent={fundFamilyEvent}
            canEditEvent={canEditEvent}
          />
        }
        badge={
          isDraft && badge ? (
            <Badge colorScheme={badge.color}>{badge.text}</Badge>
          ) : undefined
        }
        actions={
          isDraft && (
            <Restricted permission={PermissionTypeEnum.config}>
              <PageMenu>
                <MenuItem
                  className="destructive"
                  onClick={() => confirmDeleteEvent({ fundFamilyEventId })}
                >
                  {t("common:discardDraft")}
                </MenuItem>
              </PageMenu>
            </Restricted>
          )
        }
      />
      <PageBody>{children}</PageBody>
    </PageContent>
  );
};
